import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import SectionHeading from "@/components/SectionHeading";
import { ARTICLES } from "@/data/articles";

const EASE = [0.16, 1, 0.3, 1];

const EducationHub = () => {
  const [active, setActive] = useState(null);

  return (
    <section id="academy" className="py-28 lg:py-40 bg-ink-base" data-testid="academy-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <SectionHeading
          overline="The Apex Academy"
          title={<>Learn the game.<br /><span className="italic text-gold">Then change it.</span></>}
          copy="Free, plain-language education on scores, reports, and funding. No gatekeeping — this is the same material we teach in private coaching."
          testId="academy-heading"
        />

        <div className="mt-16 lg:mt-24 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {ARTICLES.map((a, i) => (
            <motion.button
              key={a.id}
              onClick={() => setActive(a)}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: (i % 3) * 0.1, ease: EASE }}
              className="group text-left bg-ink-surface border border-white/10 p-8 flex flex-col min-h-[260px] hover:border-gold/40 hover:-translate-y-1.5 transition-[border-color,transform] duration-500"
              data-testid={`article-card-${a.id}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">{a.category}</span>
                <span className="font-mono text-[10px] text-neutral-600">{a.readTime}</span>
              </div>
              <h3 className="mt-6 font-serif text-xl lg:text-2xl text-white tracking-tight leading-snug group-hover:text-gold transition-colors duration-500">
                {a.title}
              </h3>
              <p className="mt-4 text-sm text-neutral-400 font-light leading-relaxed flex-1">{a.excerpt}</p>
              <span className="mt-6 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 group-hover:text-gold transition-colors duration-300">
                Read article
                <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </motion.button>
          ))}
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent
          className="bg-ink-surface border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto p-0 sm:rounded-none [&>button]:hidden"
          data-testid="article-dialog"
        >
          <AnimatePresence>
            {active && (
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: EASE }}
                className="p-8 lg:p-12"
              >
                <div className="flex items-start justify-between gap-6">
                  <div>
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
                      {active.category} — {active.readTime}
                    </span>
                    <h3 className="mt-4 font-serif text-3xl text-white tracking-tight leading-tight">
                      {active.title}
                    </h3>
                  </div>
                  <button
                    onClick={() => setActive(null)}
                    className="text-neutral-500 hover:text-gold transition-colors shrink-0 mt-1"
                    data-testid="article-dialog-close"
                    aria-label="Close article"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="mt-8 space-y-6">
                  {active.body.map((p, i) => (
                    <p key={i} className="text-base text-neutral-300 font-light leading-relaxed">
                      {p}
                    </p>
                  ))}
                </div>
                <div className="mt-10 pt-8 border-t border-white/10">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                    The Apex Academy — Elevated Apex Co.
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default EducationHub;
